import { BrandLoader } from "@/components/brand/BrandLoader";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { roomTypeService } from "@/lib/api";
import type { DayAvailability, RoomType } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { ko } from "date-fns/locale";
import { useState } from "react";

interface AvailabilityCalendarProps {
  roomTypes: RoomType[];
}

export function AvailabilityCalendar({ roomTypes }: AvailabilityCalendarProps) {
  const today = new Date();
  const defaultFrom = format(today, "yyyy-MM-dd");
  const defaultTo = format(addDays(today, 14), "yyyy-MM-dd");

  const [selectedId, setSelectedId] = useState<string>(roomTypes[0]?.id ?? "");
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const { data, isLoading } = useQuery<DayAvailability[]>({
    queryKey: ["room-type-availability", selectedId, from, to],
    queryFn: () => roomTypeService.availability(selectedId, from, to),
    enabled: !!selectedId && !!from && !!to,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="avail-room-type">객실 타입</Label>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger id="avail-room-type" className="w-52">
              <SelectValue placeholder="타입 선택" />
            </SelectTrigger>
            <SelectContent>
              {roomTypes.map((rt) => (
                <SelectItem key={rt.id} value={rt.id}>
                  {rt.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="avail-from">시작일</Label>
          <input
            id="avail-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="avail-to">종료일</Label>
          <input
            id="avail-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="flex h-10 w-40 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70"
          />
        </div>
      </div>

      {isLoading ? (
        <BrandLoader className="py-8" size={36} label="가용 현황 불러오는 중…" />
      ) : data && data.length > 0 ? (
        <div className="overflow-x-auto">
          <div className="flex gap-2 pb-2">
            {data.map((item) => {
              const d = new Date(item.day);
              const label = format(d, "M/d", { locale: ko });
              const dow = format(d, "EEE", { locale: ko });
              const noRooms = item.total <= 0;
              const isAvail = item.available > 0;
              return (
                <div
                  key={item.day}
                  className={`flex min-w-[3.5rem] flex-col items-center rounded-lg border px-2 py-2.5 text-center ${
                    noRooms
                      ? "border-border/60 bg-muted/40"
                      : isAvail
                        ? "border-border/60 bg-card"
                        : "border-destructive/30 bg-destructive/5"
                  }`}
                >
                  <span className="text-xs text-muted-foreground">{dow}</span>
                  <span className="mt-0.5 text-sm font-medium">{label}</span>
                  <span
                    className={`mt-1 text-base font-semibold tabular-nums ${
                      noRooms
                        ? "text-muted-foreground"
                        : isAvail
                          ? "text-foreground"
                          : "text-destructive"
                    }`}
                  >
                    {noRooms ? "—" : isAvail ? item.available : "만실"}
                  </span>
                  {noRooms ? (
                    <span className="text-[10px] text-muted-foreground">객실 없음</span>
                  ) : isAvail ? (
                    <span className="text-[10px] text-muted-foreground">객실</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : !isLoading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {selectedId ? "가용 데이터가 없습니다." : "객실 타입을 선택하세요."}
        </p>
      ) : null}
    </div>
  );
}
